<template>
  <VFormItem
    :label="label"
    :layout="layout"
    :with-asterisk="withAsterisk"
  >
    <span>от</span>

    <VInputNumberRus
      ref="startInput"
      v-model="localRange.start"
      :precision="precision"
      :validate-method="validateStart"
      :disabled="disabled"
      :required="required"
      @update:modelValue="onStartUpdate"
      unit="°C"
    />

    <span>до</span>

    <VInputNumberRus
      ref="endInput"
      v-model="localRange.end"
      :precision="precision"
      :validate-method="validateEnd"
      :disabled="disabled"
      :required="required"
      @update:modelValue="onEndUpdate"
      unit="°C"
    />
  </VFormItem>
</template>


<script lang="ts">
import IntRange from "@/core/types/IntRange";
import {UnwrapNestedRefs} from "vue";

export default defineComponent({
  name: 'VTemperatureRangeInput',

  props: {
    modelValue: {
      type: Object as PropType<IntRange | UnwrapNestedRefs<IntRange>>,
      default: () => new IntRange(null, null),
    },
    label: {
      type: String,
      default: null,
    },
    layout: {
      type: String,
      default: 'vertical',
    },
    withAsterisk: {
      type: Boolean,
      default: false,
    },
    precision: {
      type: Number,
      default: 0,
    },
    min: {
      type: Number,
      default: null,
    },
    max: {
      type: Number,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  computed: {
    localRange() {
      return this.modelValue;
    },
  },

  methods: {
    validateStart(start: number | string | null) {
      const startNum = this.$core.utils.number.toNumber(start);

      if (this.required && !this.$core.utils.number.isNumber(startNum)) {
        return 'Поле обязательно для заполнения';
      }
      if (this.min !== null && this.$core.utils.number.isNumber(startNum) && startNum < this.min) {
        return `Минимальное значение: ${this.min} °C`;
      }
      const end = this.$core.utils.number.toNumber(this.localRange.end);
      if (this.$core.utils.number.isNumber(startNum) && this.$core.utils.number.isNumber(end) && startNum > end) {
        return 'Значения диапазона указаны некорректно';
      }
      return null;
    },

    validateEnd(end: number | string | null) {
      const endNum = this.$core.utils.number.toNumber(end);

      if (this.required && !this.$core.utils.number.isNumber(endNum)) {
        return 'Поле обязательно для заполнения';
      }
      if (this.max !== null && this.$core.utils.number.isNumber(endNum) && endNum > this.max) {
        return `Максимальное значение: ${this.max} °C`;
      }
      const start = this.$core.utils.number.toNumber(this.localRange.start);
      if (this.$core.utils.number.isNumber(endNum) && this.$core.utils.number.isNumber(start) && start > endNum) {
        return 'Значения диапазона указаны некорректно';
      }
      return null;
    },

    /** Ввод в левую границу — перевалидируем правую (проверка диапазона двусторонняя) */
    onStartUpdate() {
      this.$emit('update:modelValue', this.modelValue);

      this.$nextTick(() => {
        const endInput = this.$refs.endInput as any;
        if (this.$core.utils.isObject(endInput) && typeof endInput.validate === 'function') {
          endInput.validate();
        }
      });
    },

    /** Ввод в правую границу — перевалидируем левую (проверка диапазона двусторонняя) */
    onEndUpdate() {
      this.$emit('update:modelValue', this.modelValue);

      this.$nextTick(() => {
        const startInput = this.$refs.startInput as any;
        if (this.$core.utils.isObject(startInput) && typeof startInput.validate === 'function') {
          startInput.validate();
        }
      });
    },
  },
});
</script>
